import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Shield,
  LogOut,
  Check,
  Clock,
  Users,
  DollarSign,
  KeyRound,
  Loader2,
  RefreshCw,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import StatCard from "@/components/admin/StatCard";
import ChangePasswordModal from "@/components/admin/ChangePasswordModal";
import AdminLoginForm from "@/components/admin/AdminLoginForm";
import PaymentsTable from "@/components/admin/PaymentsTable";
import SuspendPinCard from "@/components/admin/SuspendPinCard";
import SuspendDialog from "@/components/admin/SuspendDialog";

export default function Admin() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [payments, setPayments] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [loading, setLoading] = useState(false);
  const [showPwModal, setShowPwModal] = useState(false);
  const [suspendTarget, setSuspendTarget] = useState(null); // payment to suspend

  const isAdmin = user?.is_admin === true;

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/payments"),
      ]);
      setStats(s.data);
      setPayments(p.data.payments);
    } catch (e) {
      toast.error("No se pudieron cargar los datos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) loadAll();
  }, [isAdmin, loadAll]);

  const confirmPayment = async (id) => {
    if (!window.confirm("¿Confirmar este pago y acreditar el plan al usuario?")) return;
    try {
      await api.put(`/admin/payments/${id}/confirm`);
      toast.success("Pago confirmado y plan acreditado");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo confirmar");
    }
  };

  const rejectPayment = async (id) => {
    const reason = window.prompt("Razón del rechazo (opcional):") || "";
    try {
      await api.put(`/admin/payments/${id}/reject?reason=${encodeURIComponent(reason)}`);
      toast.success("Pago rechazado");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo rechazar");
    }
  };

  const suspendPayment = async (reason) => {
    if (!suspendTarget) return;
    try {
      await api.put(`/admin/payments/${suspendTarget.id}/suspend`, { reason });
      toast.success("Servicio suspendido y créditos revocados");
      setSuspendTarget(null);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo suspender");
    }
  };

  if (!user || !isAdmin) {
    return <AdminLoginForm />;
  }

  const filteredPayments = payments.filter((p) => filterStatus === "all" || p.status === filterStatus);

  return (
    <div className="min-h-screen bg-zinc-100 pb-12">
      <header className="bg-zinc-900 text-white">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-red-600 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">miapoyo · admin</p>
              <p className="text-base font-black">Panel de Pagos</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-change-password"
              onClick={() => setShowPwModal(true)}
              className="text-xs font-bold text-zinc-300 hover:text-white px-3 py-2 flex items-center gap-1.5"
            >
              <KeyRound className="w-4 h-4" />
              Cambiar clave
            </button>
            <button
              data-testid="admin-logout"
              onClick={() => { logout(); navigate("/"); }}
              className="text-xs font-bold text-zinc-300 hover:text-white px-3 py-2 flex items-center gap-1.5"
            >
              <LogOut className="w-4 h-4" />
              Salir
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 pt-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <StatCard Icon={Users} label="Usuarios totales" value={stats?.total_users || 0} color="blue" />
          <StatCard Icon={Clock} label="Pagos pendientes" value={stats?.pending_payments || 0} color="amber" />
          <StatCard Icon={Check} label="Pagos confirmados" value={stats?.confirmed_payments || 0} color="emerald" />
          <StatCard Icon={DollarSign} label="Ingresos totales" value={`$${(stats?.total_revenue_usd || 0).toFixed(2)}`} color="rose" />
        </div>

        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-black text-zinc-900">Transacciones</h2>
          <div className="flex items-center gap-2">
            <select
              data-testid="admin-filter-status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="h-9 px-3 rounded-lg border-2 border-zinc-300 text-sm font-bold bg-white"
            >
              <option value="all">Todos los estados</option>
              <option value="auto_granted">Auto-acreditados (revisar)</option>
              <option value="pending_review">Pendientes (legacy)</option>
              <option value="confirmed">Confirmados</option>
              <option value="suspended">Suspendidos</option>
              <option value="rejected">Rechazados</option>
              <option value="initiated">Stripe iniciados</option>
              <option value="completed">Stripe completados</option>
            </select>
            <button
              data-testid="admin-refresh"
              onClick={loadAll}
              className="h-9 px-3 rounded-lg border-2 border-zinc-300 bg-white text-sm font-bold flex items-center gap-1.5"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Refrescar
            </button>
          </div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden">
          <PaymentsTable
            payments={filteredPayments}
            loading={loading}
            onConfirm={confirmPayment}
            onReject={rejectPayment}
            onSuspend={(p) => setSuspendTarget(p)}
          />
        </div>

        {/* Manual PIN suspension card */}
        <div className="mt-6">
          <SuspendPinCard onSuspended={loadAll} />
        </div>
      </div>

      {suspendTarget && (
        <SuspendDialog
          target={suspendTarget}
          onConfirm={suspendPayment}
          onClose={() => setSuspendTarget(null)}
        />
      )}

      {showPwModal && <ChangePasswordModal onClose={() => setShowPwModal(false)} />}
    </div>
  );
}
